import styles from '../../style.module.scss';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { includes } from 'lodash';
import { Button, ButtonsGroup, Layout, MetricItemAdvance, Modal, ModalContent, ModalFooter, ModalHeader, Text, WidgetDivider, extractErrorMessage } from '@netapp/bxp-design-system-react';
import { InfoIcon, SuccessIcon } from '@netapp/bxp-style/react-icons/Notification';
import { history } from 'store';
import { apiClient } from 'utils/clients';
import PermissionComponent from "modules/app/components/permission/PermissionComponent";
import { notificationSettings as i18nNamespace } from '../../../../i18n/namespace';
import { ConfigurationBaseCard, ConfigurationBaseFooter } from 'modules/console/configurations';
import { EmailIcon } from '@netapp/bxp-style/react-icons/2-colors-icon';
import { isAllowed } from 'utils/permissions';
import { trackConsoleFullStoryEvent } from 'utils/mrTracker';

const EmailServerConfigurationCard = () => {

    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const { selectedOrgId, selectedOrg } = useSelector(state => state.tenancy);
    const [isLoading, setIsLoading] = useState(true);
    const [emailDetails, setEmailDetails] = useState(null);
    const canViewCard = isAllowed(selectedOrg?.rules, 'notifications', 'access');
    const [error, setError] = useState(null);
    const [isRefresh, setIsRefresh] = useState(true);

    useEffect(() => {
        const fetchEmailConfig = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const response = await apiClient.get(`${process.env.REACT_APP_OCCM_ENDPOINT}organizations/${selectedOrgId}/email-config`);
                if (response?.data) {
                    setEmailDetails(response.data);
                } else {
                    setEmailDetails(null);
                }
            } catch (error) {
                dispatch({
                    type: 'NOTIFICATIONS:ERROR',
                    payload: {
                        message: t('failed_to_fetch_email_config', i18nNamespace),
                        exception: error,
                        moreInfo: extractErrorMessage(error, 'detail'),
                        key: 'GET-EMAIL-CONFIG',
                        showGlobal: true
                    }
                });
                setError(extractErrorMessage(error, 'detail'));
            } finally {
                setIsLoading(false);
            }
        }
        if (isRefresh && canViewCard) {
            setIsRefresh(false);
            fetchEmailConfig();
        }
    }, [dispatch, selectedOrgId, isRefresh, canViewCard]);

    return (
        <ConfigurationBaseCard title={t('email_server')}
            iconRenderer={() => <EmailIcon size='24px' />}
            isLoading={isLoading}
            hasNoPermission={!canViewCard}
            hasError={!!error} >
            <Layout.Grid rowGap='lg'>
                {!emailDetails && <Layout.GridItem lg={12}>
                    <Text>{t('unconfigured_email_desc')}</Text>
                </Layout.GridItem>}
                {emailDetails && (
                    <Layout.GridItem lg={emailDetails ? 3 : 12} className={styles.flexRowAlignEnd}>
                        <MetricItemAdvance
                            className={styles.metricPaddingRight5}
                            isLoading={isLoading}
                            label={t('status')}
                            labelTooltip={t('status')}
                            valueSize="small"
                            value={emailDetails ? t('configured') : t('notConfigured')}
                            valueTooltip={emailDetails ? t('configured') : t('notConfigured')}
                            valueIconTooltip={emailDetails ? t('configured') : t('notConfigured')}
                            ValueIcon={emailDetails ? SuccessIcon : InfoIcon}
                            valueIconColor={emailDetails ? 'success' : 'info'}
                            valueClassName={styles.valueLineHeight24}
                        />
                        <WidgetDivider expand />
                    </Layout.GridItem>
                )}
                {emailDetails && (
                    <Layout.GridItem lg={4} className={styles.flexRowAlignEndPaddingLeft5}>
                        <MetricItemAdvance
                            className={styles.metricPaddingRight5}
                            isLoading={isLoading}
                            label={t('connection_security')}
                            labelTooltip={t('connection_security')}
                            value={emailDetails?.startTLS ? t('STARTTLS') : t('SMTPS')}
                            valueTooltip={emailDetails?.startTLS ? t('STARTTLS') : t('SMTPS')}
                            valueSize="small"
                            valueClassName={styles.valueLineHeight24}
                        />
                        <WidgetDivider expand />
                    </Layout.GridItem>
                )}
                {emailDetails && (
                    <Layout.GridItem lg={3} className={styles.flexRowAlignEndPaddingLeft5}>
                        <MetricItemAdvance
                            className={styles.metricPaddingRight5}
                            isLoading={isLoading}
                            label={t('smtp_server')}
                            labelTooltip={t('smtp_server')}
                            value={emailDetails?.host || t('n/a')}
                            valueTooltip={emailDetails?.host || t('n/a')}
                            valueSize="small"
                            valueClassName={styles.valueLineHeight24}
                        />
                        <WidgetDivider expand />
                    </Layout.GridItem>
                )}
                {emailDetails && (
                    <Layout.GridItem lg={2} className={styles.flexRowAlignEndPaddingLeft5}>
                        <MetricItemAdvance
                            className={styles.metricPaddingRight5}
                            isLoading={isLoading}
                            label={t('port')}
                            labelTooltip={t('port')}
                            value={emailDetails?.port || t('n/a')}
                            valueTooltip={emailDetails?.port || t('n/a')}
                            valueSize="small"
                            valueClassName={styles.valueLineHeight24}
                        />
                    </Layout.GridItem>
                )}
            </Layout.Grid>
            {(
                <ConfigurationBaseFooter style={{ justifyContent: 'flex-start', borderTop: '0px', alignItems: 'flex-end', marginTop: 'auto' }}>
                    <ButtonsGroup>
                        <PermissionComponent hasPermssion={includes(selectedOrg?.rules, emailDetails ? 'bxp:notifications-modify' : 'bxp:notifications-create')}>
                            <Button color="secondary" className={styles.width152} onClick={() => {
                                if (!emailDetails) {
                                    trackConsoleFullStoryEvent({
                                        name: 'Configure-Email-Started',
                                        payload: {}
                                    });
                                }
                                history.push(`/console-configurations/email-server`);
                            }} >
                                {emailDetails ? t('edit') : t('configure')}
                            </Button>
                        </PermissionComponent>
                    </ButtonsGroup>
                </ConfigurationBaseFooter>
            )}
        </ConfigurationBaseCard>
    )
}

export default React.memo(EmailServerConfigurationCard);